import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Upload, Loader2, Sparkles, Trash2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { MoneyInput } from '@/components/ui/money-input'
import { DateInput } from '@/components/ui/date-input'
import { Combobox, type ComboOption } from '@/components/ui/combobox'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { supabase } from '@/lib/supabase'
import { useCategories, buildPicker } from '@/hooks/useCategories'
import { useTransactions, useImportStatement } from '@/hooks/useTransactions'
import { enabledCurrencyList, formatCurrency } from '@/lib/currency'
import { useHousehold } from '@/context/HouseholdContext'
import { norm } from '@/lib/text'
import { useT } from '@/i18n/I18nProvider'
import { useTranslateCategory } from '@/lib/categoryDisplay'

interface ExtractedItem {
  data?: string
  descricao?: string
  valor?: number
  moeda?: string
  parcela_n?: number
  parcela_total?: number
}
interface Extracted {
  moeda?: string
  fechamento?: string
  vencimento?: string
  itens?: ExtractedItem[]
  error?: string
}
interface Row {
  include: boolean
  date: string
  description: string
  valor: number // COM SINAL: +compra, −estorno/crédito
  currency: string // moeda DESTA linha (fatura pode ser multi-moeda)
  category_id: string | null
  installment_no: number | null
  installment_total: number | null
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '')
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

// Importa a fatura de um cartão a partir de FOTO ou PDF (extração via Gemini na
// Edge Function). Mostra um preview editável (com categoria auto-sugerida do
// histórico) e insere tudo de uma vez, já com o statement_month da fatura aberta.
export function ImportStatementDialog({
  open,
  onOpenChange,
  cardId,
  cardCurrency,
  statementMonth,
  onDone,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  cardId: string
  cardCurrency: string
  statementMonth: string // 'YYYY-MM' da fatura exibida
  onDone?: () => void | Promise<void>
}) {
  const { t } = useT()
  const tc = useTranslateCategory()
  const { data: categories } = useCategories()
  const { data: pastTxs } = useTransactions({})
  const imp = useImportStatement()
  const { enabledCurrencies } = useHousehold()
  const fileRef = useRef<HTMLInputElement>(null)

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currency, setCurrency] = useState(cardCurrency)
  const [rows, setRows] = useState<Row[]>([])
  const [fileName, setFileName] = useState('')
  const [dragOver, setDragOver] = useState(false)

  useEffect(() => {
    if (open) {
      setBusy(false)
      setError(null)
      setCurrency(cardCurrency)
      setRows([])
      setFileName('')
      setDragOver(false)
    }
  }, [open, cardCurrency])

  // Aceita só imagem ou PDF (mesmo filtro do seletor de arquivo).
  const isAccepted = (f: File) => f.type.startsWith('image/') || f.type === 'application/pdf'
  function onDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(false)
    if (busy) return
    const f = e.dataTransfer.files?.[0]
    if (!f) return
    if (!isAccepted(f)) {
      setError(t('dlg.imp.dropReject'))
      return
    }
    void onPick(f)
  }

  // Auto-categoria: descrição normalizada → categoria mais usada no histórico.
  const suggest = useMemo(() => {
    const counts = new Map<string, Map<string, number>>()
    for (const t of pastTxs ?? []) {
      if (!t.category_id || !t.description) continue
      const k = norm(t.description)
      if (!k) continue
      const m = counts.get(k) ?? new Map<string, number>()
      m.set(t.category_id, (m.get(t.category_id) ?? 0) + 1)
      counts.set(k, m)
    }
    const best = new Map<string, string>()
    for (const [k, m] of counts) {
      let top = ''
      let n = 0
      for (const [cat, c] of m) if (c > n) ((n = c), (top = cat))
      if (top) best.set(k, top)
    }
    return best
  }, [pastTxs])

  // Lançamentos JÁ existentes NESTE cartão, agrupados por VALOR|MOEDA. Para avisar
  // "já lançado". A DATA da fatura costuma diferir da do app (parcela materializada
  // cai no vencimento; a fatura mostra o consumo), então além da data igual também
  // casamos por PARCELA (n/total) ou DESCRIÇÃO. Só sinaliza — a pessoa decide.
  const existing = useMemo(() => {
    const m = new Map<string, { date: string; instN: number | null; instTotal: number | null; desc: string }[]>()
    for (const t of pastTxs ?? []) {
      if (t.account_id !== cardId || t.amount == null) continue
      const key = `${Number(t.amount).toFixed(2)}|${t.currency}`
      const arr = m.get(key) ?? []
      arr.push({
        date: t.date,
        instN: t.installment_no ?? null,
        instTotal: t.installment_total ?? null,
        desc: norm(t.description ?? ''),
      })
      m.set(key, arr)
    }
    return m
  }, [pastTxs, cardId])
  // strict = só data igual ou mesma parcela n/N (decide o desmarcado automático);
  // descrição igual sozinha não basta — assinatura mensal repete valor e descrição.
  const isDup = (r: Row, strict = false) => {
    if (!r.date) return false
    const cands = existing.get(`${Math.abs(r.valor).toFixed(2)}|${r.currency}`)
    if (!cands?.length) return false
    const rDesc = norm(r.description ?? '')
    return cands.some(
      (e) =>
        e.date === r.date ||
        (r.installment_total != null && e.instTotal === r.installment_total && e.instN === r.installment_no) ||
        (!strict && !!rDesc && e.desc === rDesc),
    )
  }
  const dupCount = useMemo(() => rows.filter((r) => isDup(r)).length, [rows, existing])

  const catOptions = useMemo(() => {
    const build = (k: 'income' | 'expense'): ComboOption[] => [
      { value: 'none', label: t('dlg.noCategory') },
      ...buildPicker(categories ?? [], k).flatMap(({ parent, children }) => [
        { value: parent.id, label: tc(parent.name), bold: true } as ComboOption,
        ...children.map((c): ComboOption => ({ value: c.id, label: tc(c.name), depth: 1, keywords: parent.name })),
      ]),
    ]
    return { expense: build('expense'), income: build('income') }
  }, [categories, t])

  async function onPick(file: File) {
    setBusy(true)
    setError(null)
    setFileName(file.name)
    try {
      const b64 = await fileToBase64(file)
      const { data, error: invErr } = await supabase.functions.invoke('extract-card-statement', {
        body: { fileBase64: b64, mimeType: file.type || 'application/octet-stream' },
      })
      if (invErr) throw new Error(t('dlg.imp.errExtract'))
      const ex = data as Extracted
      if (ex.error) throw new Error(ex.error)
      const itens = ex.itens ?? []
      if (itens.length === 0) {
        setError(t('dlg.imp.errNone'))
        setRows([])
        return
      }
      const detected = ex.moeda?.trim() || cardCurrency
      setCurrency(detected)
      // Já lançado (ex.: parcela 2/N criada ao importar a 1/N) vem DESMARCADO —
      // a pessoa ainda pode marcar se for outra compra de mesmo valor.
      setRows(
        itens.map((it) => {
          const valor = Number(it.valor) || 0
          const row: Row = {
            include: true,
            date: (it.data ?? '').slice(0, 10),
            description: it.descricao ?? '',
            valor,
            currency: it.moeda?.trim() || detected,
            category_id: suggest.get(norm(it.descricao ?? '')) ?? null,
            installment_no: it.parcela_n ?? null,
            installment_total: it.parcela_total ?? null,
          }
          return { ...row, include: !isDup(row, true) }
        }),
      )
    } catch (e) {
      setError((e as Error).message)
      setRows([])
    } finally {
      setBusy(false)
    }
  }

  const patch = (i: number, p: Partial<Row>) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...p } : r)))
  const removeRow = (i: number) => setRows((rs) => rs.filter((_, idx) => idx !== i))
  // Define a moeda de TODAS as linhas (corrige quando a IA detectou tudo errado).
  const setAllCurrency = (c: string) => {
    setCurrency(c)
    setRows((rs) => rs.map((r) => ({ ...r, currency: c })))
  }

  const chosen = rows.filter((r) => r.include && Math.abs(r.valor) > 0.005 && r.date)
  // Totais por MOEDA — uma fatura pode misturar GTQ e USD; somar junto é errado.
  const totals = useMemo(() => {
    const m = new Map<string, { compra: number; estorno: number }>()
    for (const r of chosen) {
      const e = m.get(r.currency) ?? { compra: 0, estorno: 0 }
      if (r.valor > 0) e.compra += r.valor
      else e.estorno += Math.abs(r.valor)
      m.set(r.currency, e)
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [chosen])

  // Controles reaproveitados nos dois layouts (tabela em telas largas, cartões em estreitas).
  const ctlCheck = (r: Row, i: number) => (
    <Checkbox checked={r.include} onCheckedChange={(v) => patch(i, { include: v === true })} />
  )
  const ctlDate = (r: Row, i: number) => (
    <DateInput value={r.date} onChange={(iso) => patch(i, { date: iso })} className="h-8 w-full" />
  )
  const ctlDesc = (r: Row, i: number) => (
    <div className="flex items-center gap-1.5">
      <Input
        value={r.description}
        onChange={(e) => patch(i, { description: e.target.value })}
        className="h-8 text-sm"
      />
      {r.installment_total ? (
        <span className="text-muted-foreground bg-muted shrink-0 rounded px-1 py-0.5 text-[11px] font-medium whitespace-nowrap">
          {r.installment_no ?? '?'}/{r.installment_total}
        </span>
      ) : null}
      {isDup(r) && (
        <Badge variant="outline" className="shrink-0 border-amber-500/50 text-[10px] whitespace-nowrap text-amber-600">
          {t('dlg.imp.duplicate')}
        </Badge>
      )}
    </div>
  )
  const ctlCat = (r: Row, i: number) => (
    <Combobox
      className="w-full"
      options={r.valor < 0 ? catOptions.income : catOptions.expense}
      value={r.category_id ?? 'none'}
      onChange={(v) => patch(i, { category_id: v === 'none' ? null : v })}
      placeholder={t('dlg.category')}
      searchPlaceholder={t('dlg.search')}
      aria-label={t('dlg.category')}
    />
  )
  const ctlCur = (r: Row, i: number) => (
    <Select value={r.currency} onValueChange={(c) => patch(i, { currency: c })}>
      <SelectTrigger className="h-8 w-[72px] px-2 text-xs" aria-label={t('dlg.imp.rowCurrency')}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {enabledCurrencyList(enabledCurrencies).map((c) => (
          <SelectItem key={c.code} value={c.code}>
            {c.code}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
  const ctlVal = (r: Row, i: number) => (
    <MoneyInput
      allowNegative
      value={r.valor}
      onChange={(n) => patch(i, { valor: n })}
      className={`h-8 w-full text-right ${r.valor < 0 ? 'text-success' : ''}`}
    />
  )
  const ctlDel = (i: number) => (
    <Button variant="ghost" size="icon" onClick={() => removeRow(i)} aria-label={t('dlg.imp.removeRow')}>
      <Trash2 className="text-destructive size-4" />
    </Button>
  )

  async function doImport() {
    if (chosen.length === 0) {
      toast.error(t('dlg.imp.errPickOne'))
      return
    }
    try {
      const n = await imp.mutateAsync(
        chosen.map((r) => ({
          account_id: cardId,
          category_id: r.category_id,
          kind: (r.valor < 0 ? 'income' : 'expense') as 'income' | 'expense',
          amount: Math.abs(r.valor),
          currency: r.currency,
          date: r.date,
          description: r.description.trim() || null,
          status: 'pending' as const,
          statement_month: `${statementMonth}-01`,
          installment_no: r.installment_total ? r.installment_no : null,
          installment_total: r.installment_total ?? null,
        })),
      )
      toast.success(t(n === 1 ? 'dlg.imp.importedOne' : 'dlg.imp.importedMany', { n }))
      await onDone?.()
      onOpenChange(false)
    } catch (e) {
      toast.error(t('dlg.imp.errImport'), { description: (e as Error).message })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92svh] flex-col overflow-hidden sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>{t('dlg.imp.title')}</DialogTitle>
          <DialogDescription>
            {t('dlg.imp.desc', { month: statementMonth })}
          </DialogDescription>
        </DialogHeader>

        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) void onPick(f)
            e.target.value = ''
          }}
        />

        {rows.length === 0 ? (
          <div
            onDragOver={(e) => {
              e.preventDefault()
              if (!busy) setDragOver(true)
            }}
            onDragLeave={(e) => {
              e.preventDefault()
              setDragOver(false)
            }}
            onDrop={onDrop}
            onClick={() => !busy && fileRef.current?.click()}
            className={`flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed py-10 text-center transition-colors ${
              dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
            } ${busy ? 'pointer-events-none' : ''}`}
          >
            <div className="bg-primary/10 text-primary flex size-14 items-center justify-center rounded-2xl">
              {busy ? <Loader2 className="size-7 animate-spin" /> : <Sparkles className="size-7" />}
            </div>
            <div>
              <div className="font-medium">
                {busy ? t('dlg.imp.reading') : dragOver ? t('dlg.imp.dropNow') : t('dlg.imp.sendPrompt')}
              </div>
              <p className="text-muted-foreground mx-auto max-w-sm text-sm">
                {t('dlg.imp.accepts')} · {t('dlg.imp.dropHint')}
              </p>
            </div>
            <Button
              onClick={(e) => {
                e.stopPropagation()
                fileRef.current?.click()
              }}
              disabled={busy}
            >
              <Upload className="size-4" /> {t('dlg.imp.chooseFile')}
            </Button>
            {error && <p className="text-destructive max-w-md text-sm">{error}</p>}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <div className="text-muted-foreground truncate">
                {fileName} · <b className="text-foreground">{chosen.length}</b> {t('dlg.imp.selected')}
                {dupCount > 0 && (
                  <span className="ml-2 text-amber-600">
                    {t(dupCount === 1 ? 'dlg.imp.dupWarnOne' : 'dlg.imp.dupWarnMany', { n: dupCount })}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-xs">{t('dlg.imp.applyCurrencyAll')}</span>
                <Select value={currency} onValueChange={setAllCurrency}>
                  <SelectTrigger className="h-8 w-[110px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {enabledCurrencyList(enabledCurrencies).map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={busy}>
                  <Upload className="size-4" /> {t('dlg.imp.change')}
                </Button>
              </div>
            </div>

            <div className="@container min-h-0 flex-1 overflow-auto rounded-lg border">
              {/* Diálogo largo: tabela */}
              <Table className="hidden @4xl:table">
                <TableHeader className="bg-card sticky top-0 z-10">
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead className="w-[120px]">{t('dlg.date')}</TableHead>
                    <TableHead className="min-w-[220px]">{t('dlg.description')}</TableHead>
                    <TableHead className="w-[180px]">{t('dlg.category')}</TableHead>
                    <TableHead className="w-[76px]">{t('dlg.currency')}</TableHead>
                    <TableHead className="w-[120px] text-right">{t('dlg.amount')}</TableHead>
                    <TableHead className="w-8" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={i} className={r.include ? '' : 'opacity-50'}>
                      <TableCell>{ctlCheck(r, i)}</TableCell>
                      <TableCell>{ctlDate(r, i)}</TableCell>
                      <TableCell>{ctlDesc(r, i)}</TableCell>
                      <TableCell>{ctlCat(r, i)}</TableCell>
                      <TableCell>{ctlCur(r, i)}</TableCell>
                      <TableCell className="text-right">{ctlVal(r, i)}</TableCell>
                      <TableCell>{ctlDel(i)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Diálogo estreito: cartões (sem rolagem lateral, mostra tudo empilhado) */}
              <div className="divide-y @4xl:hidden">
                {rows.map((r, i) => (
                  <div key={i} className={`space-y-2 p-2.5 ${r.include ? '' : 'opacity-50'}`}>
                    <div className="flex items-center gap-2">
                      {ctlCheck(r, i)}
                      <div className="flex-1">{ctlDate(r, i)}</div>
                      {ctlCur(r, i)}
                      {ctlDel(i)}
                    </div>
                    {ctlDesc(r, i)}
                    <div className="flex items-center gap-2">
                      <div className="min-w-0 flex-1">{ctlCat(r, i)}</div>
                      <div className="w-28">{ctlVal(r, i)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-muted-foreground text-xs">
              {totals.map(([cur, tot], idx) => (
                <span key={cur}>
                  {idx > 0 && ' · '}
                  {t('dlg.imp.purchases')} {formatCurrency(tot.compra, cur)}
                  {tot.estorno > 0 && <> · {t('dlg.imp.refunds')} {formatCurrency(tot.estorno, cur)}</>}
                </span>
              ))}
              {totals.length > 0 && <> · </>}{t('dlg.imp.negativeHint')}
            </p>
          </>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={doImport} disabled={imp.isPending || chosen.length === 0}>
            {imp.isPending ? t('dlg.imp.importing') : t('dlg.imp.importN', { n: chosen.length || '' }).trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
